import { useState, useEffect, useRef } from "react";

// ─── PHAROS VAULT CONSENT GATE ───────────────────────────────────────────────
// Drop-in React component. Wrap your app root:
//
//   import PharosVaultConsentGate from './PharosVaultConsentGate';
//
//   function App() {
//     return (
//       <PharosVaultConsentGate>
//         {/* your existing app */}
//       </PharosVaultConsentGate>
//     );
//   }
//
// Consent is stored in localStorage. Returning users skip the gate.
// To reset (e.g. after T&C update): increment CONSENT_VERSION below.
// ─────────────────────────────────────────────────────────────────────────────

const CONSENT_VERSION = "1.0";
const STORAGE_KEY = "pharos_vault_consent_v" + CONSENT_VERSION;

const CHECKBOXES = [
  {
    id: "age",
    label: "I confirm I am 18 years of age or older",
    sub: "Access is restricted to adults only.",
  },
  {
    id: "research",
    label: "I understand this Database is a research reference only — not medical advice",
    sub: "Nothing herein constitutes diagnosis, treatment, or a recommendation to use any compound.",
  },
  {
    id: "legal",
    label: "I acknowledge compound legality varies by jurisdiction and I am solely responsible for compliance with all applicable laws",
    sub: "Including TGA, SUSMP, WADA, and any applicable State or Territory legislation.",
  },
  {
    id: "terms",
    label: "I have read and agree to the Pharos Vault Legal Disclaimer & Terms of Use",
    sub: "Including the limitation of liability, indemnification, and IP provisions.",
  },
];

export default function PharosVaultConsentGate({ children }) {
  const [consented, setConsented] = useState(false);
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState({
    age: false, research: false, legal: false, terms: false,
  });
  const [accepting, setAccepting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const particlesRef = useRef([]);

  const allChecked = Object.values(checked).every(Boolean);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.accepted && data.version === CONSENT_VERSION) {
          setConsented(true);
          return;
        }
      }
    } catch (_) {}
    // Show gate with slight delay for dramatic effect
    setTimeout(() => setVisible(true), 100);
  }, []);

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function handleAccept() {
    if (!allChecked) return;
    setAccepting(true);
    const record = {
      accepted: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      jurisdiction: "Queensland, Australia",
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch (_) {}
    setTimeout(() => {
      setConsented(true);
    }, 900);
  }

  function handleDecline() {
    window.location.href = "about:blank";
  }

  if (consented) return <>{children}</>;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Pro:ital,wght@0,300;0,400;1,300&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .pv-overlay {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          background: #080c14;
          animation: pvFadeIn 0.6s ease forwards;
        }

        @keyframes pvFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .pv-overlay.accepting {
          animation: pvFadeOut 0.9s ease forwards;
        }

        @keyframes pvFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        /* Animated background grid */
        .pv-grid {
          position: absolute; inset: 0; overflow: hidden;
          background-image:
            linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px);
          background-size: 60px 60px;
          animation: pvGridShift 20s linear infinite;
        }

        @keyframes pvGridShift {
          from { background-position: 0 0; }
          to { background-position: 60px 60px; }
        }

        /* Ambient glow orbs */
        .pv-orb {
          position: absolute; border-radius: 50%;
          filter: blur(80px); pointer-events: none;
        }
        .pv-orb-1 {
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%);
          top: -200px; left: -200px;
          animation: pvOrb1 12s ease-in-out infinite alternate;
        }
        .pv-orb-2 {
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(27,42,74,0.6) 0%, transparent 70%);
          bottom: -100px; right: -100px;
          animation: pvOrb2 15s ease-in-out infinite alternate;
        }

        @keyframes pvOrb1 {
          from { transform: translate(0,0); }
          to { transform: translate(80px, 60px); }
        }
        @keyframes pvOrb2 {
          from { transform: translate(0,0); }
          to { transform: translate(-60px, -40px); }
        }

        /* Modal card */
        .pv-modal {
          position: relative; z-index: 10;
          width: min(680px, 94vw);
          max-height: 92vh;
          overflow-y: auto;
          background: linear-gradient(160deg, #0e1520 0%, #080c14 100%);
          border: 1px solid rgba(201,168,76,0.3);
          border-radius: 2px;
          box-shadow:
            0 0 0 1px rgba(201,168,76,0.08),
            0 0 60px rgba(201,168,76,0.06),
            0 40px 80px rgba(0,0,0,0.8);
          animation: pvSlideUp 0.5s cubic-bezier(0.16,1,0.3,1) 0.2s both;
        }

        @keyframes pvSlideUp {
          from { opacity: 0; transform: translateY(32px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .pv-modal::-webkit-scrollbar { width: 4px; }
        .pv-modal::-webkit-scrollbar-track { background: #080c14; }
        .pv-modal::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.3); border-radius: 2px; }

        /* Header */
        .pv-header {
          padding: 36px 40px 28px;
          border-bottom: 1px solid rgba(201,168,76,0.15);
          text-align: center;
          position: relative;
        }

        .pv-header-line {
          display: flex; align-items: center; gap: 16px;
          justify-content: center; margin-bottom: 12px;
        }

        .pv-header-rule {
          flex: 1; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent);
        }

        .pv-eye {
          width: 32px; height: 32px;
          color: #C9A84C;
          animation: pvPulse 3s ease-in-out infinite;
        }

        @keyframes pvPulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }

        .pv-title {
          font-family: 'Cinzel', serif;
          font-size: 28px; font-weight: 700;
          color: #C9A84C;
          letter-spacing: 0.14em;
          text-shadow: 0 0 30px rgba(201,168,76,0.3);
          margin-bottom: 4px;
        }

        .pv-subtitle {
          font-family: 'Cinzel', serif;
          font-size: 11px; font-weight: 400;
          color: rgba(201,168,76,0.5);
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }

        /* Warning banner */
        .pv-warning {
          margin: 24px 32px 0;
          padding: 14px 18px;
          background: rgba(201,168,76,0.06);
          border: 1px solid rgba(201,168,76,0.2);
          border-left: 3px solid #C9A84C;
          border-radius: 1px;
        }

        .pv-warning p {
          font-family: 'Crimson Pro', serif;
          font-size: 14px; line-height: 1.6;
          color: rgba(201,168,76,0.8);
          font-style: italic;
        }

        /* Body */
        .pv-body {
          padding: 28px 40px;
        }

        .pv-instruction {
          font-family: 'Cinzel', serif;
          font-size: 10px; font-weight: 600;
          letter-spacing: 0.2em;
          color: rgba(255,255,255,0.3);
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        /* Checkboxes */
        .pv-checks { display: flex; flex-direction: column; gap: 14px; }

        .pv-check-item {
          display: flex; gap: 16px; align-items: flex-start;
          padding: 16px 18px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 1px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .pv-check-item::before {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(201,168,76,0.04), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .pv-check-item:hover::before { opacity: 1; }

        .pv-check-item:hover {
          border-color: rgba(201,168,76,0.25);
        }

        .pv-check-item.is-checked {
          border-color: rgba(201,168,76,0.35);
          background: rgba(201,168,76,0.05);
        }

        .pv-checkbox {
          width: 22px; height: 22px; flex-shrink: 0;
          border: 1.5px solid rgba(201,168,76,0.4);
          border-radius: 1px;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease;
          margin-top: 1px;
        }

        .pv-check-item.is-checked .pv-checkbox {
          background: #C9A84C;
          border-color: #C9A84C;
        }

        .pv-checkmark {
          color: #080c14;
          font-size: 13px;
          font-weight: 700;
          line-height: 1;
          opacity: 0;
          transform: scale(0);
          transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1);
        }

        .pv-check-item.is-checked .pv-checkmark {
          opacity: 1;
          transform: scale(1);
        }

        .pv-check-text { flex: 1; }

        .pv-check-label {
          font-family: 'Crimson Pro', serif;
          font-size: 15px; font-weight: 400; line-height: 1.45;
          color: rgba(255,255,255,0.85);
        }

        .pv-check-sub {
          font-family: 'Crimson Pro', serif;
          font-size: 12px; font-style: italic;
          color: rgba(255,255,255,0.3);
          margin-top: 3px; line-height: 1.4;
        }

        /* Terms link */
        .pv-terms-link {
          display: inline;
          color: #C9A84C;
          text-decoration: underline;
          text-decoration-color: rgba(201,168,76,0.4);
          cursor: pointer;
          transition: color 0.2s;
        }
        .pv-terms-link:hover { color: #e8c76a; }

        /* Progress */
        .pv-progress {
          margin: 24px 0 0;
          display: flex; gap: 6px; align-items: center;
        }

        .pv-progress-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: rgba(255,255,255,0.1);
          transition: all 0.3s ease;
        }
        .pv-progress-dot.active {
          background: #C9A84C;
          box-shadow: 0 0 8px rgba(201,168,76,0.5);
        }

        .pv-progress-label {
          margin-left: 8px;
          font-family: 'Cinzel', serif;
          font-size: 10px; letter-spacing: 0.15em;
          color: rgba(255,255,255,0.25);
        }

        /* Footer / buttons */
        .pv-footer {
          padding: 24px 40px 36px;
          border-top: 1px solid rgba(201,168,76,0.1);
          display: flex; gap: 14px; align-items: center;
        }

        .pv-btn-accept {
          flex: 1;
          padding: 16px 24px;
          font-family: 'Cinzel', serif;
          font-size: 13px; font-weight: 600;
          letter-spacing: 0.15em;
          color: #080c14;
          background: linear-gradient(135deg, #C9A84C, #e8c76a, #C9A84C);
          background-size: 200% 200%;
          border: none; border-radius: 1px; cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          position: relative; overflow: hidden;
        }

        .pv-btn-accept:not(:disabled) {
          animation: pvGoldShimmer 3s ease infinite;
        }

        @keyframes pvGoldShimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .pv-btn-accept:disabled {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.2);
          cursor: not-allowed;
        }

        .pv-btn-accept:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(201,168,76,0.3);
        }

        .pv-btn-accept:not(:disabled):active {
          transform: translateY(0);
        }

        .pv-btn-decline {
          padding: 16px 20px;
          font-family: 'Cinzel', serif;
          font-size: 11px; letter-spacing: 0.12em;
          color: rgba(255,255,255,0.25);
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 1px; cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .pv-btn-decline:hover {
          color: rgba(255,255,255,0.5);
          border-color: rgba(255,255,255,0.2);
        }

        /* Terms modal */
        .pv-terms-overlay {
          position: fixed; inset: 0; z-index: 10000;
          background: rgba(0,0,0,0.85);
          display: flex; align-items: center; justify-content: center;
          animation: pvFadeIn 0.2s ease forwards;
        }

        .pv-terms-modal {
          width: min(640px, 92vw);
          max-height: 80vh;
          background: #0e1520;
          border: 1px solid rgba(201,168,76,0.25);
          border-radius: 2px;
          overflow-y: auto;
          padding: 36px 40px;
          animation: pvSlideUp 0.3s ease forwards;
        }

        .pv-terms-modal h2 {
          font-family: 'Cinzel', serif;
          font-size: 18px; color: #C9A84C;
          letter-spacing: 0.1em; margin-bottom: 20px;
        }

        .pv-terms-modal p {
          font-family: 'Crimson Pro', serif;
          font-size: 13px; line-height: 1.7;
          color: rgba(255,255,255,0.6);
          margin-bottom: 14px;
        }

        .pv-terms-modal h3 {
          font-family: 'Cinzel', serif;
          font-size: 12px; color: rgba(201,168,76,0.7);
          letter-spacing: 0.12em; margin: 20px 0 8px;
        }

        .pv-terms-close {
          margin-top: 24px;
          width: 100%; padding: 14px;
          font-family: 'Cinzel', serif;
          font-size: 12px; letter-spacing: 0.12em;
          color: #C9A84C;
          background: transparent;
          border: 1px solid rgba(201,168,76,0.3);
          border-radius: 1px; cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
        }

        .pv-terms-close:hover {
          background: rgba(201,168,76,0.08);
        }

        /* Responsive */
        @media (max-width: 520px) {
          .pv-header { padding: 28px 24px 20px; }
          .pv-body { padding: 20px 24px; }
          .pv-footer { padding: 20px 24px 28px; flex-direction: column; }
          .pv-btn-decline { width: 100%; text-align: center; }
          .pv-title { font-size: 22px; }
          .pv-warning { margin: 16px 16px 0; }
          .pv-terms-modal { padding: 24px 20px; }
        }
      `}</style>

      {/* Consent gate overlay */}
      {visible && (
        <div className={`pv-overlay${accepting ? " accepting" : ""}`}>
          <div className="pv-grid" />
          <div className="pv-orb pv-orb-1" />
          <div className="pv-orb pv-orb-2" />

          <div className="pv-modal" role="dialog" aria-modal="true" aria-label="Pharos Vault Access Agreement">

            {/* Header */}
            <div className="pv-header">
              <div className="pv-header-line">
                <div className="pv-header-rule" />
                <svg className="pv-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <div className="pv-header-rule" />
              </div>
              <div className="pv-title">PHAROS VAULT</div>
              <div className="pv-subtitle">Access Agreement · Version {CONSENT_VERSION}</div>

              <div className="pv-warning">
                <p>
                  This Database contains advanced research-grade compound information.
                  Access is restricted to qualified adults. Nothing herein constitutes medical advice,
                  treatment, or a recommendation to use any substance.
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="pv-body">
              <div className="pv-instruction">Confirm all four statements to proceed</div>

              <div className="pv-checks">
                {CHECKBOXES.map((cb, i) => (
                  <div
                    key={cb.id}
                    className={`pv-check-item${checked[cb.id] ? " is-checked" : ""}`}
                    onClick={() => toggle(cb.id)}
                    role="checkbox"
                    aria-checked={checked[cb.id]}
                    tabIndex={0}
                    onKeyDown={e => e.key === " " && toggle(cb.id)}
                    style={{ animationDelay: `${0.3 + i * 0.08}s` }}
                  >
                    <div className="pv-checkbox">
                      <span className="pv-checkmark">✓</span>
                    </div>
                    <div className="pv-check-text">
                      <div className="pv-check-label">
                        {cb.id === "terms" ? (
                          <>
                            I have read and agree to the{" "}
                            <span
                              className="pv-terms-link"
                              onClick={e => { e.stopPropagation(); setShowTerms(true); }}
                            >
                              Pharos Vault Legal Disclaimer & Terms of Use
                            </span>
                          </>
                        ) : cb.label}
                      </div>
                      <div className="pv-check-sub">{cb.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress indicator */}
              <div className="pv-progress">
                {CHECKBOXES.map(cb => (
                  <div key={cb.id} className={`pv-progress-dot${checked[cb.id] ? " active" : ""}`} />
                ))}
                <span className="pv-progress-label">
                  {Object.values(checked).filter(Boolean).length} / {CHECKBOXES.length} CONFIRMED
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="pv-footer">
              <button
                className="pv-btn-accept"
                disabled={!allChecked || accepting}
                onClick={handleAccept}
              >
                {accepting ? "Granting Access..." : "Enter the Pharos Vault"}
              </button>
              <button className="pv-btn-decline" onClick={handleDecline}>
                Decline
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Full Terms Modal */}
      {showTerms && (
        <div className="pv-terms-overlay" onClick={() => setShowTerms(false)}>
          <div className="pv-terms-modal" onClick={e => e.stopPropagation()}>
            <h2>LEGAL DISCLAIMER & TERMS OF USE</h2>

            <h3>1. NATURE OF THE DATABASE</h3>
            <p>The Pharos Vault is a proprietary reference compendium published strictly for informational and educational purposes by Spawn Bio Systems Pty Ltd.</p>

            <h3>2. NO MEDICAL ADVICE</h3>
            <p>Nothing in the Pharos Vault constitutes medical advice, diagnosis, treatment, or therapeutic recommendation of any kind. Always consult a qualified healthcare professional before using any compound referenced herein.</p>

            <h3>3. REGULATORY STATUS — TGA, SUSMP & WADA</h3>
            <p>The legal status of compounds varies by jurisdiction. In Australia, many compounds may be subject to the Therapeutic Goods Act 1989, the SUSMP, and applicable State legislation. Users are solely responsible for determining legality in their jurisdiction. Athletes are solely responsible for WADA and anti-doping compliance.</p>

            <h3>4. ASSUMPTION OF RISK</h3>
            <p>You access this Database entirely at your own risk. Research into experimental compounds may involve unknown, serious, or fatal risks. The Company accepts no liability for any outcome arising from reliance on this information.</p>

            <h3>5. LIMITATION OF LIABILITY</h3>
            <p>To the maximum extent permitted by law, the Company's aggregate liability is limited to AUD $100.00. The Company is not liable for direct, indirect, incidental, consequential, or punitive damages.</p>

            <h3>6. INTELLECTUAL PROPERTY</h3>
            <p>All content is the intellectual property of Spawn Bio Systems Pty Ltd. Reproduction, redistribution, scraping, or use as AI/ML training data is strictly prohibited.</p>

            <h3>7. GOVERNING LAW</h3>
            <p>These Terms are governed by the laws of Queensland, Australia. You submit to the exclusive jurisdiction of the courts of Queensland.</p>

            <p style={{ color: "rgba(201,168,76,0.5)", fontStyle: "italic", fontSize: "12px" }}>
              Effective Date: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} · Version {CONSENT_VERSION} · Spawn Bio Systems Pty Ltd
            </p>

            <button className="pv-terms-close" onClick={() => setShowTerms(false)}>
              Close & Return to Agreement
            </button>
          </div>
        </div>
      )}
    </>
  );
}
